import { format } from 'date-fns';
import omit from 'lodash/omit';
import { API } from '../api';
import { IFetchPageQuery } from '../IFetchQueryOptions';
import { IPagination } from '../IPagination';
import { ICreateProgram, IProgram, IUniqueProgramOperator, IUniqueProgramType, IUpdateProgramToProductRelation, IUpdateProgram } from './interface';
import { DATE_FORMAT_CSV } from '../commonConst';
import { exportCSVData } from '../common/exportCSVData';
import { castArray } from 'lodash';

export const fetchPrograms = async (
    { sorter, pagination, search, include, filter, company_id }: IFetchPageQuery,
    signal?: AbortSignal | null
): Promise<IPagination<IProgram>> => {
    const limit = pagination?.pageSize || 1000;
    const current = pagination?.current || 1;
    const offset = (current - 1) * limit;
    if (!sorter?.field || !sorter?.order) {
        sorter = {};
    }

    const queryParams: Record<string, string> = {
        ...(sorter?.field && { sortField: sorter.field, sortOrder: sorter.order }),
        ...filter,
        ...(search && { search }),
        ...(include && { include }),
        ...(company_id && { company_ids: `"${[company_id].join()}"` }),
        offset: offset.toString(),
        limit: limit.toString(),
    };

    if (filter?.utility_customer_id !== undefined) {
        const utility_customer_id = castArray(filter?.utility_customer_id);

        delete queryParams.utility_customer_id;
        queryParams.utility_customer_ids = `"${utility_customer_id.join(',')}"`;
    }

    if (filter?.utility_automation !== undefined) {
        const isEmailUtilityAutomation = filter?.utility_automation.startsWith('email:'); // @Hack as email utility automations starts with "email:" but cloudVenIs not.

        if (!isEmailUtilityAutomation) {
            queryParams.cloud_ven_id = filter.utility_automation;
            delete queryParams.utility_automation;
        }
    }

    const path = `/programs?${new URLSearchParams(queryParams)}`;

    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot load list of programs!`);
};

export const fetchProgramOperators = async (signal?: AbortSignal | null): Promise<IUniqueProgramOperator[]> => {
    const response = await API.fetch(`/programs/operators`, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot load list of programs operators!`);
};

export const fetchProgramsByCompanyId = async ({ company_id }: IFetchPageQuery, signal?: AbortSignal | null): Promise<IProgram[]> => {
    const path = `/programs/byId?${new URLSearchParams({
        ...(company_id && { companyId: company_id.toString() }),
    })}`;
    const response = await API.fetch(path, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot load list of programs by company!`);
};

export const fetchProgramTypes = async (signal?: AbortSignal | null): Promise<IUniqueProgramType[]> => {
    const response = await API.fetch(`/programs/types`, {
        ...(signal && { signal }),
        headers: {
            'Content-Type': 'application/json',
        },
    });

    const respBody: any = await response.json();

    if (response.ok) {
        return respBody;
    }

    throw new Error(respBody?.error?.message || `Cannot load list of programs types!`);
};

export const deleteProgram = async (program: IProgram) => {
    const response = await API.fetch(`/programs/${program.program_id}`, {
        method: 'delete',
        headers: {
            'Content-Type': 'application/json',
        },
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();
    throw new Error(respBody?.error?.message || `Cannot delete program!`);
};

export const createProgram = async (program: ICreateProgram) => {
    const response = await API.fetch(`/programs`, {
        method: 'post',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(program),
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return;
    }

    throw new Error(respBody?.error?.message || `Cannot save program!`);
};

export const updateProgram = async (program: IUpdateProgram) => {
    const response = await API.fetch(`/programs/${program.program_id}`, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(omit(program, 'program_id')),
    });
    const respBody: any = await response.json();

    if (response.ok) {
        return;
    }

    throw new Error(respBody?.error?.message || `Cannot update program!`);
};

export const batchUpdateProgramToProductRelation = async (relations: IUpdateProgramToProductRelation[]) => {
    const response = await API.fetch(`/programs/products`, {
        method: 'put',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({ relations: relations }),
    });

    if (response.ok) {
        return;
    }

    const respBody: any = await response.json();

    throw new Error(respBody?.error?.message || 'Cannot update links between program and product!');
};

export const exportPrograms = async ({ company_id, search, filter = {} }: IFetchPageQuery) => {
    const queryParams: Record<string, string> = {
        format: 'csv',
        ...filter,
        ...(search && { search }),
        ...(company_id && { company_ids: `"${[company_id].join()}"` }),
        include: 'enrolled-sites-count,products,utility_customer,cloud_ven',
    };

    if (filter?.utility_customer_id !== undefined) {
        const utility_customer_id = castArray(filter?.utility_customer_id);

        delete queryParams.utility_customer_id;
        queryParams.utility_customer_ids = `"${utility_customer_id.join(',')}"`;
    }

    if (filter?.utility_automation !== undefined) {
        const isEmailUtilityAutomation = filter?.utility_automation.startsWith('email:'); // @Hack as email utility automations starts with "email:" but cloudVenIs not.

        if (!isEmailUtilityAutomation) {
            queryParams.cloud_ven_id = filter.utility_automation;
            delete queryParams.utility_automation;
        }
    }

    const response = await API.fetch(`/programs?${new URLSearchParams(queryParams)}`, {
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    const respBody: string = await response.text();

    if (response.ok) {
        const filename = `programs--${format(new Date(), DATE_FORMAT_CSV)}.csv`;
        exportCSVData(respBody, filename);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};

export const exportProgramToProductRelations = async () => {
    const queryParams: Record<string, string> = {
        format: 'csv',
        include: 'products',
        exportColumnPreset: 'program_to_product_relations',
    };

    const response = await API.fetch(`/programs?${new URLSearchParams(queryParams)}`, {
        headers: {
            'Content-Type': 'text/csv',
        },
    });

    const respBody: string = await response.text();

    if (response.ok) {
        const filename = `program-to-product-relations--${format(new Date(), DATE_FORMAT_CSV)}.csv`;
        exportCSVData(respBody, filename);
    } else {
        const respBody: any = await response.json();
        throw new Error(respBody?.error?.message || `Cannot export data!`);
    }
};
